<template>
  <div class="shell">
    <!-- Decorative floating peels -->
    <div class="peel peel--1" aria-hidden="true">🍌</div>
    <div class="peel peel--2" aria-hidden="true">🍌</div>

    <header class="nav" :class="{ 'nav--scrolled': scrolled }">
      <div class="nav__inner container-wide">
        <router-link to="/" class="brand" @click="menuOpen = false">
          <span class="brand__mark">🍌</span>
          <span class="brand__text">
            La&nbsp;Banane
            <span class="brand__sub">le revival</span>
          </span>
        </router-link>

        <button
          class="nav__burger"
          :aria-expanded="menuOpen"
          aria-label="Menu"
          @click="menuOpen = !menuOpen"
        >
          <span :class="{ open: menuOpen }"></span>
        </button>

        <nav class="nav__links" :class="{ open: menuOpen }" @click="menuOpen = false">
          <router-link to="/" class="nav__link">Accueil</router-link>
          <router-link to="/aleatoire" class="nav__link">Au hasard</router-link>
          <router-link to="/classement" class="nav__link">Classement</router-link>
          <router-link to="/rechercher" class="nav__link">Rechercher</router-link>
          <router-link to="/proposer" class="nav__link nav__link--accent">Proposer</router-link>
          <router-link to="/about" class="nav__link">L'histoire</router-link>
          <router-link to="/peut-on-rire-de-tout" class="nav__link">Peut-on rire de tout&nbsp;?</router-link>
          <router-link v-if="auth.user && auth.user.isStaff" to="/admin" class="nav__link">Admin</router-link>

          <a
            v-if="showAppDownload"
            :href="apkUrl"
            class="nav__link nav__link--app"
            download
          >📱 Appli Android</a>

          <div class="nav__userzone">
            <template v-if="auth.user">
              <router-link to="/profil" class="nav__user" :title="auth.user.email">
                <span class="nav__avatar">{{ initial }}</span>
                {{ auth.user.displayName || auth.user.email }}
              </router-link>
              <button class="nav__logout" @click="onLogout">Déconnexion</button>
            </template>
            <router-link v-else to="/login" class="nav__link nav__link--login">Connexion</router-link>
          </div>
        </nav>
      </div>
    </header>

    <main class="shell__main">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component"></component>
        </transition>
      </router-view>
    </main>

    <footer class="foot">
      <div class="container-wide foot__inner">
        <div class="foot__col">
          <div class="foot__logo">🍌 La Banane</div>
          <p class="foot__tag">Le site qui vous colle la banane.<br>La vraie, la dure, celle qui dure.</p>
        </div>
        <div class="foot__col foot__links">
          <router-link to="/aleatoire">Une blague au hasard</router-link>
          <router-link to="/classement">Le Top &amp; le Flop</router-link>
          <router-link to="/proposer">Proposer une blague</router-link>
          <router-link to="/peut-on-rire-de-tout">Peut-on rire de tout&nbsp;?</router-link>
          <router-link to="/about">L'histoire de labanane.org</router-link>
          <a v-if="showAppDownload" :href="apkUrl" class="foot__app" download>
            📱 Télécharger l'appli Android
          </a>
        </div>
        <div class="foot__col foot__credit">
          <p>
            Un hommage à <strong>labanane.org</strong> (2001–2017),
            créé à l'origine par <strong>remouk</strong>.
          </p>
          <p class="foot__small">
            Les blagues ne nous appartiennent pas — préservation, pas appropriation.
            Un ayant droit&nbsp;? <router-link to="/about">Contactez-nous</router-link>.
          </p>
        </div>
      </div>
      <div class="foot__baseline">
        Ressuscité avec 🍌 et beaucoup de nostalgie · {{ year }}
      </div>
    </footer>
  </div>
</template>

<script>
import { authState, logout } from '@/auth'
import { isInApp, APK_URL } from '@/twa'

export default {
  name: 'AppShell',
  data() {
    return {
      auth: authState,
      menuOpen: false,
      scrolled: false,
      year: 2026,
      showAppDownload: !isInApp(),
      apkUrl: APK_URL,
    }
  },
  computed: {
    initial() {
      const u = this.auth.user
      const s = (u?.displayName || u?.email || '?').trim()
      return s.charAt(0).toUpperCase()
    },
  },
  mounted() {
    this.onScroll()
    window.addEventListener('scroll', this.onScroll, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    document.body.classList.remove('nav-locked')
  },
  watch: {
    // Lock page scroll while the mobile drawer is open — otherwise the body
    // scrolls behind the fixed panel and the mobile URL bar collapsing makes
    // the drawer's bottom flicker / disappear.
    menuOpen(open) {
      document.body.classList.toggle('nav-locked', open)
    },
    // Close the drawer whenever we navigate to another route.
    $route() {
      this.menuOpen = false
    },
  },
  methods: {
    onScroll() { this.scrolled = window.scrollY > 12 },
    onLogout() {
      logout()
      if (this.$route.meta.requiresAuth) this.$router.push('/')
    },
  },
}
</script>

<style scoped>
.shell { display: flex; flex-direction: column; min-height: 100vh; position: relative; overflow-x: clip; }

/* Floating decorative peels */
.peel {
  position: fixed;
  font-size: 8rem;
  opacity: 0.07;
  pointer-events: none;
  z-index: 0;
  filter: saturate(1.4);
}
.peel--1 { top: 14%; left: -3%; transform: rotate(-18deg); animation: float1 14s ease-in-out infinite; }
.peel--2 { bottom: 10%; right: -2%; transform: rotate(24deg); animation: float2 18s ease-in-out infinite; }
@keyframes float1 { 50% { transform: rotate(-10deg) translateY(24px); } }
@keyframes float2 { 50% { transform: rotate(18deg) translateY(-28px); } }

/* ---- NAV ---- */
.nav {
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.3s var(--ease-out);
  background: transparent;
}
.nav--scrolled {
  background: rgba(255, 253, 247, 0.82);
  backdrop-filter: blur(12px) saturate(1.3);
  border-bottom: var(--border-w) solid var(--border-c);
  box-shadow: 0 4px 0 rgba(139, 91, 0, 0.1);
}
.nav__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 24px;
  /* The nav bar spans the full viewport width so the user zone can sit flush
     against the right edge (overrides the 1080px .container-wide cap). */
  max-width: none;
  width: 100%;
}

.brand { display: flex; align-items: center; gap: 10px; }
.brand__mark { font-size: 2rem; transition: transform 0.4s var(--ease-bounce); }
.brand:hover .brand__mark { transform: rotate(-20deg) scale(1.15); }
.brand__text {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.5rem;
  line-height: 0.9;
  letter-spacing: -0.03em;
  color: var(--ink-900);
  display: flex;
  flex-direction: column;
}
.brand__sub {
  font-family: var(--font-accent);
  font-style: italic;
  font-weight: 500;
  font-size: 0.72rem;
  letter-spacing: 0;
  color: var(--banana-700);
}

.nav__links { display: flex; align-items: center; gap: 2px; flex: 1; }
.nav__userzone { display: flex; align-items: center; gap: 12px; margin-left: auto; }
.nav__link {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.95rem;
  padding: 8px 12px;
  border-radius: var(--r-pill);
  color: var(--ink-800);
  transition: background 0.2s, color 0.2s;
  position: relative;
  white-space: nowrap;
}
.nav__link:hover { background: var(--banana-200); }
.nav__link.router-link-exact-active { background: var(--choco); color: var(--banana-400); }
.nav__link--accent {
  background: var(--banana-700);
  color: #fff;
}
.nav__link--accent:hover { background: var(--banana-800); }
.nav__link--accent.router-link-exact-active { background: var(--banana-800); color: #fff; }
.nav__link--login { border: var(--border-w) solid var(--border-c); }
/* APK download promoted in the mobile drawer only (desktop keeps it in footer) */
.nav__link--app {
  display: none;
  background: var(--banana-700);
  color: #fff;
  font-weight: 700;
}
.nav__link--app:hover { background: var(--banana-800); color: #fff; }

.nav__user { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; font-size: 0.9rem; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nav__avatar {
  width: 30px; height: 30px; flex: none;
  display: grid; place-items: center;
  background: var(--banana-700);
  color: #fff;
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 0.85rem;
}
.nav__logout {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.85rem;
  padding: 6px 12px;
  border: var(--border-w) solid var(--border-c);
  background: var(--cream);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: background 0.2s;
}
.nav__logout:hover { background: var(--bruise); color: #fff; }

.nav__burger { display: none; background: none; border: none; cursor: pointer; padding: 8px; }
.nav__burger span, .nav__burger span::before, .nav__burger span::after {
  content: ''; display: block; width: 26px; height: 3px; background: var(--ink-900); border-radius: 2px; transition: 0.3s var(--ease-out);
}
.nav__burger span::before { transform: translateY(-8px); }
.nav__burger span::after { transform: translateY(5px); }
.nav__burger span.open { background: transparent; }
.nav__burger span.open::before { transform: rotate(45deg); }
.nav__burger span.open::after { transform: rotate(-45deg) translateY(-3px); }

.shell__main { flex: 1; position: relative; z-index: 1; padding-bottom: 40px; }

/* ---- FOOTER ---- */
.foot {
  position: relative; z-index: 1;
  background: var(--choco);
  color: var(--banana-100);
  margin-top: 60px;
  border-top: 3px solid var(--banana-500);
}
/* Grain matter on the large brown fill — screen blend so the noise reads as a
   subtle warm texture on a dark surface (multiply would vanish on dark). */
.foot::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.06;
  mix-blend-mode: screen;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='fn'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23fn)'/%3E%3C/svg%3E");
}
.foot > * { position: relative; z-index: 1; }
.foot__inner { display: grid; grid-template-columns: 1.2fr 1fr 1.4fr; gap: 40px; padding: 48px 24px 32px; }
.foot__logo { font-family: var(--font-display); font-weight: 800; font-size: 1.6rem; color: var(--banana-400); margin-bottom: 10px; }
.foot__tag { font-family: var(--font-accent); font-style: italic; color: var(--banana-200); opacity: 0.85; }
.foot__links { display: flex; flex-direction: column; gap: 10px; }
.foot__links a { font-weight: 600; opacity: 0.85; transition: opacity 0.2s, color 0.2s; width: fit-content; }
.foot__links a:hover { opacity: 1; color: var(--banana-400); }
.foot__app {
  margin-top: 6px;
  padding: 8px 14px;
  background: var(--banana-700);
  color: #fff !important;
  border-radius: var(--r-pill);
  font-weight: 700 !important;
  opacity: 1 !important;
}
.foot__app:hover { background: var(--banana-800); color: #fff !important; }
.foot__credit p { font-size: 0.9rem; opacity: 0.8; margin: 0 0 10px; }
.foot__credit strong { color: var(--banana-300); }
.foot__small { font-size: 0.8rem !important; opacity: 0.6 !important; }
.foot__small a { text-decoration: underline; }
.foot__baseline { text-align: center; padding: 18px; font-size: 0.85rem; border-top: 1px solid rgba(255,255,255,0.1); opacity: 0.6; }

/* Page transition */
.page-enter-active, .page-leave-active { transition: opacity 0.25s var(--ease-out), transform 0.25s var(--ease-out); }
.page-enter-from { opacity: 0; transform: translateY(10px); }
.page-leave-to { opacity: 0; transform: translateY(-6px); }

/* ---- Responsive ---- */
@media (max-width: 860px) {
  .nav__burger { display: block; z-index: 110; }
  .nav__links {
    position: fixed;
    top: 0; right: 0; bottom: 0;
    height: 100dvh;
    width: min(80vw, 320px);
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
    padding: 80px 28px;
    background: var(--cream);
    border-left: var(--border-w) solid var(--border-c);
    transform: translateX(100%);
    transition: transform 0.35s var(--ease-out);
    box-shadow: -20px 0 60px -20px rgba(0,0,0,0.4);
    overflow-y: auto;
  }
  .nav__links.open { transform: translateX(0); }
  .nav__userzone {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-left: 0;
    width: 100%;
  }
  .nav__link, .nav__logout { width: 100%; font-size: 1.1rem; }
  .nav__link--app { display: block; text-align: left; margin-top: 4px; }
  .nav__user { max-width: 100%; font-size: 1rem; margin-bottom: 4px; }
  .foot__inner { grid-template-columns: 1fr; gap: 28px; }
}

@media (max-width: 560px) {
  .nav__inner { padding: 12px 16px; }
  .brand__text { font-size: 1.3rem; }
  .brand__mark { font-size: 1.7rem; }
  .foot__inner { padding: 36px 20px 28px; }
  /* rein in the huge decorative peels so they never nudge layout */
  .peel { font-size: 5rem; }
}

</style>
